import React, {useState, useEffect } from "react";
import PropTypes from "prop-types";
import clsx from "clsx";
import { Formik } from "formik";
import {
  Button,
  Card,
  CardHeader,
  CardContent,
  Grid,
  TextField,
  makeStyles,
  Divider,
} from "@material-ui/core";
import Autocomplete from '@material-ui/lab/Autocomplete';

import { SIREN_CONFIG } from "config.js";
import { loadRoomList, getRoomName, loadCaregiverList, getCaregiverValue, getCaregiverTelNo } from "utils/LoadConfiguration";
import {useSelector} from 'react-redux';
import { FormattedMessage, useIntl } from "react-intl";


const PatientUpdateForm = (props) => {
  const { className, history, patientInfo,  ...rest } = props;
  const classes = useStyles();
  const {formatMessage} = useIntl();


	const [caregiverList, setCaregiverList] = useState([]);
	const [selectedCaregiver1_Id, setSelectedCaregiver1_Id] = useState('');

  const [roomList, setRoomList] = useState([]);
	const [selectedRoomId, setSelectedRoomId] = useState('');
  const facility_id = useSelector((store) => store.user.facility_id);

  const handleSubmit = (event, values) => {
		event.preventDefault();

    fetch(`${SIREN_CONFIG.HOSTNAME}/patient/updatePatient`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        facility_id: facility_id,
        patient_id: values.patientId,
        patient_name: values.patientName,
        patient_room_id: selectedRoomId,
        patient_room_name: values.patientRoomName,
        patient_tel_no: values.patientTelNo,
        patient_postcode: values.patientPostCode,
        patient_addr1: values.patientAddr1,
        patient_addr2: values.patientAddr2,
        protector1_name: values.protector1_name,
        protector1_tel_no: values.protector1_tel_no,
				caregiver1_Id: selectedCaregiver1_Id,
      }),
    }).then((response) => {
        return response.json();
		}).then((responseData) => {
        if (responseData.resultCode === "000") {
          alert(formatMessage({id:'update_successful'}));

          setTimeout(() => {
            history.goBack();
          }, 500);
        } else {
          //에러
          console.log(responseData.message);
          alert(`${formatMessage({id:'update_failed'})}\n${responseData.resultMessage}\n${responseData.message}`);
        }
		}).catch((error) => {
			console.error("error:" , error);
      alert(`${formatMessage({id:'update_failed'})}\n${responseData.message}`);
		});
  };


  const handleDelete = () => {

    fetch(`${SIREN_CONFIG.HOSTNAME}/patient/deletePatient`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        facility_id: facility_id,
        patientId: patientInfo.patientId,
      }),
    }).then((response) => {
        return response.json();
		}).then((responseData) => {
			if (responseData.resultCode === "000") {
        alert(formatMessage({id:'delete_successful'}));

				setTimeout(() => {
					history.goBack();
				}, 500);
			} else {
				//에러
        console.log(responseData.message);
        alert(`${formatMessage({id:'delete_failed'})}\n${responseData.resultMessage}\n${responseData.message}`);
			}
		}).catch((error) => {
        console.error(error);
        alert(`${formatMessage({id:'delete_failed'})}\n${responseData.message}`);
		});
  }

	const cancelConfirm = () => console.log("delete canceled.");

	const useConfirm = (msg = "", onConfirm, onCancel) => {
		if (!onConfirm || typeof onConfirm !== "function") return;
		if (!onCancel || typeof onCancel !== "function") return;

		const doDelete = () => {
			if(window.confirm(msg)){
				onConfirm();
			} else {
				onCancel();
			}
		}

		return doDelete;
	};


	const confirmDelete = useConfirm( formatMessage({id:'question_delete'}) ,
		handleDelete,
		cancelConfirm);


  const handleBack = () => {
    history.goBack();
  };


	useEffect(()=> {
		setSelectedCaregiver1_Id(patientInfo.caregiver1_Id);

    async function loadCaregiverInfo(){
      setCaregiverList( await loadCaregiverList(facility_id ) );
    }
    loadCaregiverInfo();


    setSelectedRoomId(patientInfo.patientRoomId);
    async function loadRoomInfo() {
      setRoomList( await loadRoomList(facility_id ) );
    }

    loadRoomInfo();

	},[]);


  return (
    <Formik
      initialValues={{
        patientId: patientInfo.patientId,
        patientName: patientInfo.patientName,
        patientRoomName: patientInfo.patientRoomName,
        patientTelNo: patientInfo.patientTelNo,
        patientPostCode: patientInfo.patientPostCode,
        patientAddr1: patientInfo.patientAddr1,
        patientAddr2: patientInfo.patientAddr2,
        protector1_name: patientInfo.protector1_name,
        protector1_tel_no: patientInfo.protector1_tel_no,
				caregiver1_Id: selectedCaregiver1_Id,
      }}
    >
      {({
        errors,
        handleBlur,
        handleChange,
        isSubmitting,
        touched,
        values,
      }) => (
        <form
          className={clsx(classes.root, className)}
          onSubmit={(e) => handleSubmit(e, values)}
        >
          <Card>
          <CardHeader
              className={classes.card_header}
              title={formatMessage({id:'patient_info'})}
              titleTypographyProps={{align:"center",  color: 'inherit',  variant:'h4'}}
            />

            <CardContent>
              <Grid container spacing={2}>
                <Grid item md={3} xs={12}>
                  <TextField
                    error={Boolean(touched.patientId && errors.patientId)}
                    fullWidth
                    helperText={touched.patientId && errors.patientId}
                    label="ID"
                    disabled
                    name="patientId"
                    onBlur={handleBlur}
                    onChange={handleChange}
                    required
                    value={values.patientId}
                    variant="outlined"
                    inputProps={{ maxLength: 10 }}
                  />
                </Grid>

                <Grid item md={3} xs={12}>
                  <TextField
                    error={Boolean(touched.patientName && errors.patientName)}
                    fullWidth
                    helperText={touched.patientName && errors.patientName}
                    label={formatMessage({id:'patient_name'})}
                    name="patientName"
                    onBlur={handleBlur}
                    onChange={handleChange}
                    required
                    value={values.patientName}
                    variant="outlined"
                    inputProps={{ maxLength: 10 }}
                  />
                </Grid>

                <Grid item md={3} xs={12}>
                  <Autocomplete
                    id="select-room"
                    // style={{ width: 300 }}
                    options={ roomList }
                    classes={{ option: classes.options, }}
                    autoHighlight
                    getOptionLabel={(option) => (option.roomName ? option.roomName : "") }
                    getOptionSelected={ (option, value) => option.roomName === value.roomName }
                    value={ getRoomName(selectedRoomId) }
                    renderOption={(option) => (
                      <React.Fragment>
                        {option.roomName}
                      </React.Fragment>
                    )}
                    renderInput={(params) => (
                      <TextField
                      {...params}
                      label={formatMessage({id:'emergency_history_select_room'})}
                      variant="outlined"
                      required
                      inputProps={{
                        ...params.inputProps,
                        // autoComplete: 'new-password', // disable autocomplete and autofill
                      }}
                      />
                    )}

                    onChange={(event, newValue) => {
                      if (newValue && newValue.roomId){
                        setSelectedRoomId(newValue.roomId);
                      }
                    }}
                  />
                </Grid>

                <Grid item md={3} xs={12}>
                <TextField
                    error={Boolean(touched.patientTelNo && errors.patientTelNo)}
                    fullWidth
                    helperText={touched.patientTelNo && errors.patientTelNo}
                    label={formatMessage({id:'patient_tel'})}
                    name="patientTelNo"
                    onBlur={handleBlur}
                    onChange={handleChange}
                    value={values.patientTelNo}
                    variant="outlined"
                    inputProps={{ maxLength: 20 }}
                  />
                </Grid>

                <Grid item md={2} xs={12}>
                  <TextField
                    error={Boolean(touched.patientPostCode && errors.patientPostCode)}
                    fullWidth
                    helperText={touched.patientPostCode && errors.patientPostCode}
                    label={formatMessage({id:'patient_postcode'})}
                    name="patientPostCode"
                    onBlur={handleBlur}
                    onChange={handleChange}
                    value={values.patientPostCode}
                    variant="outlined"
                    inputProps={{ maxLength: 5 }}
                  />
                </Grid>

                <Grid item md={5} xs={12}>
                  <TextField
                    error={Boolean(touched.patientAddr1 && errors.patientAddr1)}
                    fullWidth
                    helperText={touched.patientAddr1 && errors.patientAddr1}
                    lable={formatMessage({id:'patient_addr1'})}
                    name="patientAddr1"
                    onBlur={handleBlur}
                    onChange={handleChange}
                    value={values.patientAddr1}
                    variant="outlined"
                    inputProps={{ maxLength: 100 }}
                  />
                </Grid>

                <Grid item md={5} xs={12}>
                  <TextField
                    error={Boolean(touched.patientAddr2 && errors.patientAddr2)}
                    fullWidth
                    helperText={touched.patientAddr2 && errors.patientAddr2}
                    label={formatMessage({id:'patient_addr2'})}
                    name="patientAddr2"
                    onBlur={handleBlur}
                    onChange={handleChange}
                    value={values.patientAddr2}
                    variant="outlined"
                    inputProps={{ maxLength: 100 }}
                  />
                </Grid>


                <Grid item lg={12} xs={12}>

                  <Divider className={classes.divider}/>

                  <CardHeader
                      className={classes.card_sub_header}
                      title={formatMessage({id:'protector_info'})}
                      titleTypographyProps={{align:"center",  color: 'inherit',  variant:'h5'}}/>
                </Grid>


                <Grid container item spacing={2} >
                  <Grid item lg={3} xs={12}>
                    <TextField
                      error={Boolean(touched.protector1_name && errors.protector1_name)}
                      fullWidth
                      helperText={touched.protector1_name && errors.protector1_name}
                      label={formatMessage({id:'protector_name'})}
                      name="protector1_name"
                      onBlur={handleBlur}
                      onChange={handleChange}
                      value={values.protector1_name}
                      variant="outlined"
                      inputProps={{ maxLength: 10 }}
                    />
                  </Grid>

                  <Grid item md={3} xs={12}>
                    <TextField
                      error={Boolean(touched.protector1_tel_no && errors.protector1_tel_no)}
                      fullWidth
                      helperText={touched.protector1_tel_no && errors.protector1_tel_no}
                      label={formatMessage({id:'protector_tel'})}
                      name="protector1_tel_no"
                      onBlur={handleBlur}
                      onChange={handleChange}
                      value={values.protector1_tel_no}
                      variant="outlined"
                      inputProps={{ maxLength: 20 }}
                    />
                  </Grid>

                  <Grid item md={6} xs={12}/>
                </Grid>


                <Grid item md={12} xs={12}>
                  <Divider className={classes.divider}/>
                  <CardHeader
                      className={classes.card_sub_header}
                      title={formatMessage({id:'patient_caregiver_info'})}
                      titleTypographyProps={{align:"center",  color: 'inherit',  variant:'h5'}}/>
                </Grid>

                <Grid container item spacing={2} >
                  <Grid item md={3} xs={12} >

                    <Autocomplete
                      id="caregiver1"
                      options={ caregiverList }
                      classes={{ option: classes.options, }}
											autoHighlight
                      getOptionLabel={(option) => option.caregiverName ? option.caregiverName : "" }
											getOptionSelected={ (option, value) => option.caregiverName === value.caregiverName }
											value={ getCaregiverValue(selectedCaregiver1_Id) }

                      renderInput={(params) => (
                        <TextField
                        {...params}
                        label={formatMessage({id:'patient_select_caregiver'})}
                        variant="outlined"
                        fullWidth
                        inputProps={{
                          ...params.inputProps,
                          autoComplete: 'new-password', // disable autocomplete and autofill
                        }}
                        />
                      )}

                      onChange={(event, newValue) => {
												if (newValue && newValue.caregiverId){
													setSelectedCaregiver1_Id(newValue.caregiverId);
													console.log('newValue: ', newValue);
													// values.caregiver1_telno = newValue.caregiverTelNo;
												}
                      }}
                  />

                  </Grid>

                  <Grid item md={3} xs={12}>
                    <TextField
											InputLabelProps={{ shrink: true }}
											disabled
                      fullWidth
                      label={formatMessage({id:'patient_caregiver_tel'})}
                      name="caregiver1_telno"
                      value={ getCaregiverTelNo(selectedCaregiver1_Id) }
                      variant="outlined"
                      inputProps={{ maxLength: 20 }}
                    />
                  </Grid>

                  <Grid item md={6} xs={12}/>
                </Grid>

              </Grid>


              <Grid container className={classes.action}>
                <Grid item md={8} xs={12}>
                  <Button
                    variant="contained"
                    color="primary"
                    type="submit"
                    size="large"
                    disabled={isSubmitting}
                    className={classes.action}
                  >
                    <FormattedMessage id="update" />
                  </Button>

                  <Button
                    variant="outlined"
                    size="large"
                    onClick={handleBack}
                    className={classes.action}
                  >
                    <FormattedMessage id="cancel" />
                  </Button>

                </Grid>

                <Grid item align="right" md={4} xs={12}>
                  <Button
                    variant="contained"
                    color="secondary"
                    size="large"
                    className={classes.action}
										onClick={confirmDelete}
                  >
                    <FormattedMessage id="delete" />
                  </Button>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </form>
      )}
    </Formik>
  );
};

PatientUpdateForm.propTypes = {
  className: PropTypes.string,
  history: PropTypes.object,
};

export default PatientUpdateForm;


const useStyles = makeStyles((theme) => ({
  root: {
    padding: theme.spacing(2),
  },
  content: {
    marginTop: theme.spacing(2),
  },
  action: {
    marginTop: theme.spacing(1),
    marginBottom: theme.spacing(1),
    "& + &": {
      marginLeft: theme.spacing(1),
    },
  },

  card_header: {
    color: theme.palette.primary.contrastText,
    backgroundColor: theme.palette.primary.main,
  },
  card_sub_header : {
    color: theme.palette.primary.contrastText,
    backgroundColor: theme.palette.grey.main,

    marginTop: theme.spacing(2),
  },
  divider: {
    marginTop: theme.spacing(2),
  }
}));
